// AGC: BiometricPromptModal - Post-login modal to enable biometric (Yitix-style)
// AGC: Enable / Not Now (re-prompt in 7 days) / Don't Ask Again

import React, { useState, useEffect } from 'react'
import { useTranslation } from '../../hooks/useTranslation'
import { useBiometric, useAuth } from '../../hooks'
import {
  isBiometricEnabled,
  setBiometricEnabled,
  shouldShowBiometricPrompt,
  recordBiometricPromptShown,
  dismissBiometricPrompt,
} from '../../services/biometricPreferences'
import { Fingerprint, ScanFace, X } from 'lucide-react'

interface BiometricPromptModalProps {
  onClose: () => void
}

/**
 * AGC: BiometricPromptModal - "Enable Biometric?" overlay after login
 * AGC: Shown when shouldShowBiometricPrompt() and user just logged in
 */
export const BiometricPromptModal: React.FC<BiometricPromptModalProps> = ({ onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const {
    isAvailable,
    isLoading,
    enrollBiometric,
    clearError,
    biometricType,
  } = useBiometric()

  const [error, setError] = useState<string | null>(null)

  const handleEnable = async () => {
    if (!user?.uid || !isAvailable) return
    clearError()
    setError(null)
    try {
      // AGC: Verify hardware works, then enroll and enable
      const success = await enrollBiometric(user.uid)
      if (success) {
        setBiometricEnabled(true)
        onClose()
      } else {
        setError(t('profile.errors.biometricSetupFailed') || 'Failed to setup biometric')
      }
    } catch (err: any) {
      setError(err?.message || t('profile.errors.biometricSetupFailed'))
    }
  }

  const handleNotNow = () => {
    recordBiometricPromptShown()
    onClose()
  }

  const handleDontAskAgain = () => {
    dismissBiometricPrompt()
    onClose()
  }

  const Icon = () => (
    <div
      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
      style={{ backgroundColor: 'var(--accent-light-bg)' }}
    >
      {biometricType === 'face' ? (
        <ScanFace className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
      ) : (
        <Fingerprint className="w-8 h-8" style={{ color: 'var(--accent-primary)' }} />
      )}
    </div>
  )

  // AGC: Don't show if biometric not available (e.g. web)
  useEffect(() => {
    if (!isAvailable) {
      onClose()
    }
  }, [isAvailable, onClose])

  if (!isAvailable) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-sm w-full p-6 border border-gray-200 dark:border-gray-700">
        <Icon />
        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
          {t('biometric.enableTitle')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 text-center text-sm mb-6">
          {t('biometric.enableDescription')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            onClick={handleEnable}
            disabled={isLoading}
            className="w-full py-3 px-4 rounded-xl font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-primary)' }}
          >
            {isLoading ? t('common.loading') : t('biometric.enable')}
          </button>
          <button
            onClick={handleNotNow}
            disabled={isLoading}
            className="w-full py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {t('biometric.notNow')}
          </button>
          <button
            onClick={handleDontAskAgain}
            disabled={isLoading}
            className="w-full py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
          >
            {t('biometric.dontAskAgain')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default BiometricPromptModal
